'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

// Fallback assets
const fallbackImg = "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=1440&h=400&fit=crop&auto=format";
const imgVector51 = "/hero-divider.svg";

interface HeroSectionProps {
  featuredProjects?: any[];
}

export default function HeroSection({ featuredProjects = [] }: HeroSectionProps) {
  const heroRef = useScrollAnimation({ threshold: 0.3 });
  
  // Find the project marked as featured in hero, or use the first one
  const heroProject = featuredProjects.find((project: any) => project.featuredInHero) || featuredProjects[0];
  const title = heroProject?.title || "The Impossible Garden";
  const author = heroProject?.projectAuthor || "Maya Chen";
  const year = heroProject?.projectYear || "2025";
  const projectUrl = heroProject?.projectUrl;
  // Use toolsUsed ACF repeater field
  const projectTools = heroProject?.toolsUsed || [];
  const backgroundSrc = heroProject?.heroBackground?.sourceUrl || 
                       heroProject?.featuredImage?.node?.sourceUrl || 
                       fallbackImg;

  // Determine the link destination
  const projectLink = heroProject?.slug ? `/projects/${heroProject.slug}` : null;

  const bannerContent = (
    <>
      {/* Background container with subtle zoom */}
      <div className="absolute inset-0 transition-opacity duration-700 ease-image hero-background smooth-transform overflow-hidden">
        {/* Background Image with Next.js Image component */}
        <Image
          src={backgroundSrc}
          alt={title}
          fill
          className="object-cover"
          priority
          fetchPriority="high"
          sizes="100vw"
          quality={90}
        />
        
        {/* Mobile gradient overlay - Visible only on mobile */}
        <div
          className="absolute inset-0 sm:hidden pointer-events-none"
          style={{
            background: 'linear-gradient(179.728deg, rgba(0, 0, 0, 0) 38.223%, rgba(0, 0, 0, 0.6) 75.324%)',
          }}
        />
        
        {/* Default state gradient overlay - Hidden on tablets */}
        <div
          className="absolute inset-0 transition-opacity duration-400 ease-premium hidden sm:block md:opacity-0 lg:opacity-100 lg:group-hover:opacity-0 will-change-auto pointer-events-none"
          style={{
            background: 'linear-gradient(173.603deg, rgba(0, 0, 0, 0) 86.692%, rgba(0, 0, 0, 0.4) 97.648%)',
          }}
        />
        
        {/* Hover/Tablet state gradient overlay - Always visible on tablets */}
        <div
          className="absolute inset-0 hidden sm:block md:opacity-100 lg:opacity-0 transition-opacity duration-400 ease-premium lg:group-hover:opacity-100 will-change-auto pointer-events-none"
          style={{
            background: 'linear-gradient(90deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.2) 100%), linear-gradient(197.767deg, rgba(0, 0, 0, 0) 67.017%, rgba(0, 0, 0, 0.4) 90.063%)',
          }}
        />
      </div>

      {/* Default state attribution - bottom right - Hidden on mobile and tablets */}
      <div className="absolute bottom-0 right-0 transition-opacity duration-300 hidden lg:block lg:opacity-100 lg:group-hover:opacity-0">
        <div className="flex flex-row items-center justify-center relative">
          <div className="box-border flex flex-row gap-2.5 items-center justify-center pb-3 pr-3 sm:pb-5 sm:pr-5">
            <div className="font-ibm-plex-sans-condensed font-medium italic text-[#f6f4f1] text-fluid-xs tracking-wide">
              <p className="leading-relaxed whitespace-nowrap">
                <span className="font-medium italic">{`"${title}", `}</span>
                <span className="font-medium not-italic">
                  {author}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile story card overlay - Visible only on mobile */}
      <div className="absolute inset-0 flex flex-col items-center justify-end pb-[9px] px-6 sm:hidden">
        <div className="flex flex-col gap-2 items-center justify-end w-full max-w-[310px]">
          <div className="min-h-[93px] relative w-full ring-1 ring-inset ring-white">
            <div className="flex flex-col items-center justify-center relative size-full">
              <div className="flex flex-col min-h-[93px] items-center justify-center p-[20px] relative w-full">
                <div className="flex flex-col gap-4 items-center justify-start pb-5 pt-0 px-0 relative w-full">
                  <div className="font-gilda-display leading-[0] not-italic relative text-white text-[24px] text-center">
                    <p className="block leading-[32px] capitalize">{title}</p>
                  </div>
                </div>
                <div className="font-ibm-plex-sans-condensed leading-[0] not-italic relative text-white text-[12px] text-center tracking-[1.2px] uppercase">
                  <p className="underline underline-offset-2 block leading-[14px]">
                    VIEW PROJECT
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="w-full text-right">
            <div className="font-ibm-plex-sans-condensed italic leading-[0] relative text-white text-[9px] tracking-[0.9px]">
              <p className="block leading-[14px]">by {author}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content overlay - Always visible on tablets, hover on desktop */}
      <div className="absolute bottom-3 md:bottom-4 lg:bottom-5 left-1/2 transform -translate-x-1/2 w-full max-w-[1440px] px-5 h-[100px] md:h-[110px] lg:h-[133px] 
                  hidden sm:block
                  md:opacity-100 md:translate-y-0
                  lg:opacity-0 lg:translate-y-2 lg:group-hover:opacity-100 lg:group-hover:translate-y-0
                  transition-all duration-300 ease-premium">
        <div className="relative size-full" style={{ boxShadow: 'inset 0 0 0 1px #ffffff' }}>
          <div className="box-border flex flex-col gap-2.5 items-start justify-start p-6 md:p-7 lg:p-9 relative size-full">
              <div className="box-border flex flex-row items-center justify-between p-0 relative shrink-0 w-full">
                {/* Left side - Title and author */}
                <div className="box-border flex flex-col gap-1.5 items-start justify-start leading-[0] not-italic p-0 relative text-white text-nowrap">
                  <div className="font-gilda-display relative shrink-0 text-[24px] md:text-[28px] lg:text-[36px] text-left">
                    <p className="block leading-[32px] md:leading-[38px] lg:leading-[48px] text-nowrap whitespace-pre capitalize">
                    {title}
                  </p>
                </div>
                  <div className="flex flex-col font-ibm-plex-sans-condensed justify-center relative shrink-0 text-[14px] text-left tracking-[1.4px] uppercase">
                    <p className="leading-[24px] text-nowrap whitespace-pre">
                      <span>by </span>
                      <span className="underline decoration-solid underline-offset-[from-font]">
                      {author}
                    </span>
                    <span className="tracking-[1.4px]">, {year}</span>
                  </p>
                </div>
              </div>

                {/* Right side - Tools and button */}
                <div className="flex flex-row items-center self-stretch flex-shrink-0">
                  <div className="box-border flex flex-row gap-6 h-full items-center justify-end p-0 relative">
                  {/* Divider */}
                  <div className="h-[40px] md:h-[50px] lg:h-[61px] w-0 relative shrink-0">
                    <div className="absolute bottom-0 left-[-0.5px] right-[-0.5px] top-0">
                      <Image
                        alt=""
                        src={imgVector51}
                        width={1}
                        height={61}
                        className="block max-w-none size-full"
                      />
                    </div>
                  </div>

                    {/* Tools section */}
                    <div className="flex flex-row gap-4 items-center justify-start p-0 relative">
                      <div className="font-ibm-plex-sans-condensed text-white text-[14px] tracking-[1.4px] uppercase flex-shrink-0">
                        <p className="whitespace-nowrap">
                        Tools Used:
                      </p>
                    </div>
                      <div className="box-border flex flex-row gap-2 items-center justify-start p-0 relative flex-wrap max-w-[400px]">
                        {projectTools && Array.isArray(projectTools) && projectTools.length > 0 ? (
                          projectTools.slice(0, 2).map((tool: any, index: number) => {
                            // Handle both toolsUsed format and projectTools format
                            const toolIconSrc = tool.toolIcon?.sourceUrl || 
                                              tool.toolIcon?.mediaItemUrl || 
                                              tool.icon?.sourceUrl || 
                                              null;
                            const toolName = tool.toolName || tool.name || `Tool ${index + 1}`;
                            
                            return (
                              <div key={index} className="bg-white box-border flex flex-row gap-2 items-center justify-center px-[9.333px] py-2 relative shrink-0 size-10">
                                {toolIconSrc ? (
                                  <div className="h-[17.333px] relative shrink-0 w-[21.333px]">
                                    <Image
                                      alt={toolName}
                                      src={toolIconSrc}
                                      width={21}
                                      height={17}
                                      className="block max-w-none size-full object-contain"
                                    />
                                  </div>
                                ) : (
                                  // Placeholder icon if no icon is available
                                  <div className="h-[17.333px] relative shrink-0 w-[21.333px] flex items-center justify-center">
                                    <div className="w-4 h-4 bg-gray-400 rounded-full" />
                                  </div>
                                )}
                              </div>
                            );
                          })
                        ) : (
                          // Default placeholder icons if no tools are assigned
                          <>
                            <div className="bg-white box-border flex items-center justify-center px-[9.333px] py-2 relative shrink-0 size-10">
                              <div className="h-[17.333px] relative shrink-0 w-[21.333px] flex items-center justify-center">
                                <div className="w-4 h-4 bg-gray-400 rounded-full" />
                              </div>
                            </div>
                            <div className="bg-white box-border flex items-center justify-center px-[9.333px] py-2 relative shrink-0 size-10">
                              <div className="h-[17.333px] relative shrink-0 w-[21.333px] flex items-center justify-center">
                                <div className="w-4 h-4 bg-gray-400 rounded-full" />
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="h-[40px] md:h-[50px] lg:h-[61px] w-0 relative shrink-0 ml-4 md:ml-5 lg:ml-6 mr-6 md:mr-8 lg:mr-11">
                    <div className="absolute bottom-0 left-[-0.5px] right-[-0.5px] top-0">
                      <Image
                        alt=""
                        src={imgVector51}
                        width={1}
                        height={61}
                        className="block max-w-none size-full"
                      />
                    </div>
                  </div>

                    {/* View Project button */}
                    <div
                      className="box-border flex flex-row gap-2.5 h-10 items-center justify-center px-5 md:px-6 py-2.5 relative shrink-0 w-[140px] cursor-pointer hover:bg-white transition-all duration-300 group/btn"
                      style={{ boxShadow: 'inset 0 0 0 1px #ffffff' }}
                      onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (projectLink) {
                        window.location.href = projectLink;
                      } else if (projectUrl) {
                        window.open(projectUrl, '_blank');
                      }
                      }}
                    >
                      <div className="font-ibm-plex-sans-condensed font-medium leading-[0] not-italic relative shrink-0 text-white group-hover/btn:text-black text-[12px] md:text-[13px] lg:text-[14px] text-center text-nowrap tracking-[1.2px] md:tracking-[1.3px] lg:tracking-[1.4px] uppercase transition-colors duration-300">
                        <p className="block leading-none whitespace-pre">
                          VIEW PROJECT
                        </p>
                      </div>
                    </div>
                </div>
              </div>
            </div>
          </div>
        </div>
    </>
  );

  return (
    <div className="w-full flex justify-center">
      {projectLink ? (
        <Link href={projectLink} className="block w-full max-w-[1440px]">
          <div 
            ref={heroRef}
            className="relative w-full h-[250px] sm:h-[300px] md:h-[350px] lg:h-[400px] xl:h-[450px] group cursor-pointer bg-white transition-all duration-400 ease-premium hover:shadow-premium overflow-hidden" 
            data-name="Hero" 
            id="node-810_1997"
          >
            {bannerContent}
          </div>
        </Link>
      ) : (
        <div 
          ref={heroRef}
          className="relative w-full max-w-[1440px] h-[250px] sm:h-[300px] md:h-[350px] lg:h-[400px] xl:h-[450px] group cursor-pointer bg-white transition-all duration-400 ease-premium hover:shadow-premium overflow-hidden" 
          data-name="Hero" 
          id="node-810_1997"
          onClick={projectUrl ? () => window.open(projectUrl, '_blank') : undefined}
        >
          {bannerContent}
        </div>
      )}
    </div>
  );
}